import ReactDOM from "react-dom";
import { useCallback, useEffect, useRef, useState } from "react";
import { Row, Col } from "react-bootstrap";
import { APIClient } from "api/apiCore";
import * as url from "api/urls"
import { backdropStyle, modalStyle } from "DialogProviderShare";
import { Utils } from "helpers/utils";
import { useBackButtonClose, useRedux } from "hooks";
import Loader from "components/Loader";
import SelectDropdownEmp from "components/SelectDropdownEmp";
import Select from "react-select";
import { TaskStatus } from "../src/redux/workplace/wpTypes";

interface TaskApproverData {
  taskId?: string;
  taskName?: string;
  approveStatus?: string | null;
  approveRemark?: string | null;
  requestBy?: number | null;
  reqName?: string | null;
  reqHId?: number | null;
  reqHName?: string | null;
  respondId?: number | null;
  respName?: string | null;
  respHId?: number | null;
  respHName?: string | null;
  assignerId?: number | null;
  assignerName?: string | null;
  approvalFlow?: string | null;
  approveStatusMapping?: string | null; // raw jsonb text from tb_tktask.ApproveStatusMapping, null = ยังไม่ผูก
  docType?: string | null;
  approveOutside?: boolean; // tb_doctype.approveoutside of the task's doc type - approval is done in ERP, not here
}

const APPROVE_OUTSIDE_MESSAGE = "เอกสารประเภทนี้ต้องอนุมัติผ่านระบบ ERP โดยตรง กรุณาไปอนุมัติในระบบ ERP";

// Keys match the raw json_agg/row_to_json output from sp_findapproverbytaskid (lowercase, not camelCase)
interface ApprovalFlowStep {
  seq: number;
  userid: number;
  username: string;
  rolename: string;
  roledisplayname: string;
  userfullname: string;
  action: string;
  actiondate: string | null;
  remark: string | null;
}

interface ApproverRow {
  id: number;
  caption: string;
  userId: number | null;
}

const CAPTION_SUBMITTER = "ผู้ยื่นขออนุมัติ";
//const CAPTION_ROW1 = "ผู้อนุมัติ ของาน";
//const CAPTION_ROW2 = "ผู้อนุมัติ ทำงาน";
const CAPTION_ROW1 = "หัวหน้า ผู้ของาน";
const CAPTION_ROW2 = "หัวหน้า ผู้ทำงาน";
const CAPTION_DEFAULT_NEW_ROW = "ผู้ตรวจสอบ";

/* ---------------- Approve status -> Task status mapping ---------------- */
// "Rejected" is the value the backend stores; the UI label is "Reject"
const APPROVE_STATUSES = ["Open", "Cancel", "Rejected", "Finished"] as const;
type ApproveStatusKey = typeof APPROVE_STATUSES[number];

// null = None = ไม่ผูก = do not change task status when the approve status becomes this
type StatusMapping = Record<ApproveStatusKey, TaskStatus | null>;

const DEFAULT_STATUS_MAPPING: StatusMapping = {
  Open: null,
  Cancel: TaskStatus.Cancel,
  Rejected: TaskStatus.Cancel,
  Finished: TaskStatus.Finished,
};

const APPROVE_STATUS_LABEL: Record<ApproveStatusKey, string> = {
  Open: "Open",
  Cancel: "Cancel",
  Rejected: "Reject",
  Finished: "Finished",
};

// tb_tktask.ApproveStatusMapping as saved by the backend, e.g. {"Open":null,"Cancel":3,...}
const parseStatusMapping = (json?: string | null): StatusMapping | null => {
  if (!json || !json.trim()) return null;
  try {
    const raw = JSON.parse(json);
    if (!raw || typeof raw !== "object") return null;
    const result = {} as StatusMapping;
    APPROVE_STATUSES.forEach(k => { result[k] = Utils.GetTaskStatusFromVal(Utils.ToNumber(raw[k], -1) ?? -1); });
    return result;
  } catch (e) {
    return null;
  }
}

export function TaskApproverPortal({ taskId, onClose }: { taskId: string, onClose: (v: any) => void }) {
  const { useAppSelector } = useRedux();
  const myDisplayName = useAppSelector((state: any) => state.Profile2.displayName2 ?? state.Profile2.displayName);
  const myUserId = useAppSelector((state: any) => state.Profile2.userId);
  const isAdmin = useAppSelector((state: any) => state.Profile2.isAdmin);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [data, setData] = useState<TaskApproverData | null>(null);
  const [rows, setRows] = useState<ApproverRow[]>([]);

  const idRef = useRef<number>(0);
  const nextId = () => ++idRef.current;

  const buildDefaultRows = (reqHId: number | null, respHId: number | null): ApproverRow[] => {
    const result: ApproverRow[] = [];
    if (reqHId && reqHId !== respHId) {
      result.push({ id: nextId(), caption: CAPTION_ROW1, userId: reqHId });
    }
    if (respHId) {
      result.push({ id: nextId(), caption: CAPTION_ROW2, userId: respHId });
    }
    if (result.length === 0) {
      result.push({ id: nextId(), caption: CAPTION_ROW1, userId: null });
      result.push({ id: nextId(), caption: CAPTION_ROW2, userId: null });
    }
    return result;
  }

  const [showAddApprover, setShowAddApprover] = useState<boolean>(false);
  const [showRemoveApprover, setShowRemoveApprover] = useState<boolean>(false);

  // null = ยังไม่ได้ผูกสถานะ (not yet mapped). Loaded from tb_tktask.ApproveStatusMapping;
  // saved with Send Approval while composing, or directly via WP_TK_SAVE_STATUS_MAPPING while Open.
  const [statusMapping, setStatusMapping] = useState<StatusMapping | null>(null);
  const [showStatusMapping, setShowStatusMapping] = useState<boolean>(false);

  const mappedKeys = statusMapping ? APPROVE_STATUSES.filter(k => statusMapping[k] != null) : [];
  const mappedText = mappedKeys.map(k => APPROVE_STATUS_LABEL[k]).join(", ");
  const mappedTitle = mappedKeys.map(k => `${APPROVE_STATUS_LABEL[k]} → ${Utils.GetTaskStatusText(statusMapping![k])}`).join("\n");

  useBackButtonClose(true, () => onClose(null));

  const loadData = useCallback(() => {
    try {
      setIsLoading(true);
      new APIClient().fnPostForm(url.WP_TK_FIND_APPROVER, { taskId })
        .then(rs => {
          setData(rs ?? null);
          setRows(buildDefaultRows(rs?.reqHId ?? null, rs?.respHId ?? null));
          setStatusMapping(parseStatusMapping(rs?.approveStatusMapping));
          setIsLoading(false);
        })
        .catch(e2 => {
          Utils.HandleException(e2, true);
          setIsLoading(false);
        });
    } catch (e) {
      Utils.HandleException(e, true);
      setIsLoading(false);
    }
  }, [taskId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleEsc = useCallback((event: any) => {
    if (event.key === "Escape") onClose(null);
  }, [onClose]);

  useEffect(() => {
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [handleEsc]);

  // Every action that records an approval decision or changes the approve status goes through this.
  // The backend checks the same rule, this just stops the user before the request.
  const isBlockedByApproveOutside = (): boolean => {
    if (!data?.approveOutside) return false;
    Utils.SweetAlert(APPROVE_OUTSIDE_MESSAGE);
    return true;
  }

  const isNotYetSendApprove = !data?.approveStatus || data.approveStatus.trim() === "" || data.approveStatus === "Cancel" || data.approveStatus === "Rejected";

  // Compose mode: mapping is held locally and saved together with Send Approval.
  // Open (flow in progress): mapping is saved on its own, right when the user clicks ตกลง.
  // Finished: the mapping has already been applied - show it read-only.
  const canEditStatusMapping = isNotYetSendApprove || data?.approveStatus === "Open";

  const onStatusMappingOk = async (result: StatusMapping) => {
    if (isNotYetSendApprove) {
      setStatusMapping(result);
      return;
    }

    try {
      setIsLoading(true);
      await new APIClient().fnPostForm(url.WP_TK_SAVE_STATUS_MAPPING, { taskId, statusMapping: JSON.stringify(result) });
      setStatusMapping(result);
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      Utils.HandleException(e, true);
    }
  }

  const getApprovalFlowSteps = (): ApprovalFlowStep[] => {
    try {
      return JSON.parse(data?.approvalFlow ?? "") ?? [];
    } catch (e) {
      return [];
    }
  }

  const onRemoveRow = (rowId: number) => {
    setRows(prev => prev.length <= 1 ? prev : prev.filter(r => r.id !== rowId));
  }

  const stripCaptionSuffix = (caption: string) => caption.replace(/\s*#\d+$/, "");

  const onAddRow = (rowId: number, insertBefore: boolean) => {
    const idx = rows.findIndex(r => r.id === rowId);
    const insertAt = idx < 0 ? rows.length : (insertBefore ? idx : idx + 1);

    let defaultCaption: string;
    if (insertBefore) {
      defaultCaption = CAPTION_DEFAULT_NEW_ROW;
    } else {
      const clickedRow = idx >= 0 ? rows[idx] : null;
      const baseCaption = clickedRow ? stripCaptionSuffix(clickedRow.caption) : CAPTION_DEFAULT_NEW_ROW;
      const matchCount = rows.filter(r => stripCaptionSuffix(r.caption) === baseCaption).length;
      defaultCaption = `${baseCaption} #${matchCount}`;
    }

    const caption = window.prompt("ระบุชื่อตำแหน่งผู้อนุมัติ", defaultCaption);
    if (caption === null) return; // cancelled
    const newRow: ApproverRow = { id: nextId(), caption: caption.trim() || defaultCaption, userId: null };
    setRows(prev => {
      const copy = [...prev];
      copy.splice(insertAt, 0, newRow);
      return copy;
    });
  }

  const onChangeRowUser = (rowId: number, userId: number | null) => {
    setRows(prev => prev.map(r => r.id === rowId ? { ...r, userId } : r));
  }

  const onEditCaption = (rowId: number, currentCaption: string) => {
    const caption = window.prompt("ระบุชื่อตำแหน่งผู้อนุมัติ", currentCaption);
    if (caption === null) return; // cancelled
    const trimmed = caption.trim();
    if (!trimmed) return;
    setRows(prev => prev.map(r => r.id === rowId ? { ...r, caption: trimmed } : r));
  }

  const onMoveRow = (rowId: number, direction: -1 | 1) => {
    setRows(prev => {
      const idx = prev.findIndex(r => r.id === rowId);
      const swapWith = idx + direction;
      if (idx < 0 || swapWith < 0 || swapWith >= prev.length) return prev;
      const copy = [...prev];
      [copy[idx], copy[swapWith]] = [copy[swapWith], copy[idx]];
      return copy;
    });
  }

  const onOk = async () => {
    if (isBlockedByApproveOutside()) return;
    const selectedRows = rows.filter(r => r.userId);
    if (selectedRows.length === 0) {
      Utils.SweetAlert("กรุณาเลือกผู้อนุมัติอย่างน้อย 1 คน");
      return;
    }
    const userIds = selectedRows.map(r => r.userId);
    if (new Set(userIds).size !== userIds.length) {
      Utils.SweetAlert("ไม่สามารถเลือกผู้อนุมัติคนเดียวกันซ้ำในหลายขั้นตอนได้");
      return;
    }

    try {
      setIsLoading(true);
      const steps = selectedRows.map(r => ({ userId: r.userId, roleName: r.caption }));
      // "" = ไม่ผูกสถานะ - the backend clears the column, so a resend after Cancel/Reject can also drop a previous mapping.
      await new APIClient().fnPostForm(url.WP_TK_SEND_APPROVAL, {
        taskId,
        steps: JSON.stringify(steps),
        statusMapping: statusMapping ? JSON.stringify(statusMapping) : "",
      });
      setIsLoading(false);
      onClose({ approvers: selectedRows.map(r => ({ caption: r.caption, userId: r.userId })) });
    } catch (e) {
      setIsLoading(false);
      Utils.HandleException(e, true);
    }
  }

  const onCancelApproval = async () => {
    if (isBlockedByApproveOutside()) return;
    const reason = window.prompt("ระบุเหตุผลที่ยกเลิกการขออนุมัติ");
    if (reason === null) return; // cancelled prompt
    const trimmed = reason.trim();
    if (!trimmed) {
      Utils.SweetAlert("กรุณาระบุเหตุผลที่ยกเลิก");
      return;
    }

    try {
      setIsLoading(true);
      await new APIClient().fnPostForm(url.WP_TK_CANCEL_APPROVAL, { taskId, reason: trimmed });
      setIsLoading(false);
      onClose({ cancelled: true });
    } catch (e) {
      setIsLoading(false);
      Utils.HandleException(e, true);
    }
  }

  const onApproveStep = async () => {
    if (isBlockedByApproveOutside()) return;
    try {
      setIsLoading(true);
      await new APIClient().fnPostForm(url.WP_TK_APPROVE_STEP, { taskId });
      setIsLoading(false);
      onClose({ approved: true });
    } catch (e) {
      setIsLoading(false);
      Utils.HandleException(e, true);
    }
  }

  const onRejectStep = async () => {
    if (isBlockedByApproveOutside()) return;
    const reason = window.prompt("ระบุเหตุผลที่ปฏิเสธการอนุมัติ");
    if (reason === null) return; // cancelled prompt
    const trimmed = reason.trim();
    if (!trimmed) {
      Utils.SweetAlert("กรุณาระบุเหตุผลที่ปฏิเสธ");
      return;
    }

    try {
      setIsLoading(true);
      await new APIClient().fnPostForm(url.WP_TK_REJECT_STEP, { taskId, reason: trimmed });
      setIsLoading(false);
      onClose({ rejected: true });
    } catch (e) {
      setIsLoading(false);
      Utils.HandleException(e, true);
    }
  }

  const onRecallStep = async () => {
    if (isBlockedByApproveOutside()) return;
    try {
      setIsLoading(true);
      await new APIClient().fnPostForm(url.WP_TK_RECALL_STEP, { taskId });
      setIsLoading(false);
      onClose({ recalled: true });
    } catch (e) {
      setIsLoading(false);
      Utils.HandleException(e, true);
    }
  }

  return ReactDOM.createPortal(
    <div style={backdropStyle}>
      {isLoading && (<Loader />)}
      <div style={modalStyle}>
        <div
          className="d-flex align-items-start justify-content-between"
          style={{ gap: 8, borderBottom: "dashed 1px var(--bs-secondary)", marginBottom: "0.5rem" }}
        >
          <h3 style={{ margin: 0, minWidth: 0 }}>
            ขออนุมัติ ทำงาน
            {data?.taskName && (<div className="fz-10 text-secondary">{data.taskName}</div>)}
          </h3>
          <span
            className={`hand hover badge-task-status-head ${mappedText ? "text-primary" : "text-secondary"}`}
            style={{ flex: "0 0 auto" }}
            title={mappedTitle || (canEditStatusMapping ? "ตั้งค่าการเปลี่ยนสถานะงานตามสถานะการอนุมัติ" : "ไม่ได้ผูกสถานะงานกับสถานะการอนุมัติ")}
            onClick={() => setShowStatusMapping(true)}
          >
            <i className={`fas ${mappedText ? "fa-link" : "fa-unlink"} me-1`}></i>
            {mappedText ? `Status Map: ${mappedText}` : "Status Mapping"}
          </span>
        </div>

        <Row className="mb-2">
          <Col xs={12} md={4} className="mb-2 mb-md-0">
            <div className="fz-10 text-secondary">ผู้มอบหมายงาน</div>
            <div>{data?.assignerName || "-"}</div>
          </Col>
          <Col xs={12} md={4} className="mb-2 mb-md-0">
            <div className="fz-10 text-secondary">ผู้ของาน</div>
            <div>{data?.reqName || "-"}</div>
          </Col>
          <Col xs={12} md={4}>
            <div className="fz-10 text-secondary">ผู้รับผิดชอบงาน</div>
            <div>{data?.respName || "-"}</div>
          </Col>
        </Row>

        <div style={{ borderBottom: "dashed 1px var(--bs-secondary)", marginBottom: 8 }}></div>

        {data?.approveOutside && (
          <div className="py-2 px-3 mb-2 rounded" style={{ backgroundColor: "var(--m2-warning-box-bg)", color: "var(--m2-warning-box-text)", border: "solid 1px var(--m2-warning-box-border)" }}>
            <i className="fas fa-exclamation-triangle me-1"></i>{APPROVE_OUTSIDE_MESSAGE}
          </div>
        )}

        {isNotYetSendApprove && (
          <div>
            <div className="mb-2">
              <div className="text-secondary">{CAPTION_SUBMITTER}</div>
              <div className="d-flex align-items-center" style={{ gap: 4 }}>
                <div style={{ flex: 1 }}>{myDisplayName || "-"}</div>
              </div>
            </div>
            {rows.map((row, index) => (
              <div key={row.id} className="mb-2">
                <div className="hand hover text-primary" title="คลิกเพื่อแก้ไขชื่อตำแหน่ง" onClick={() => onEditCaption(row.id, row.caption)}>{row.caption}</div>
                <div className="d-flex align-items-center" style={{ gap: 4 }}>
                  <div style={{ flex: 1 }}>
                    <SelectDropdownEmp isDisabled={false} selectedUserId={row.userId} onUserChanged={(e) => onChangeRowUser(row.id, e)} />
                  </div>
                  <button type="button" className="btn btn-outline-secondary btn-sm" title="เลื่อนขึ้น" disabled={index === 0} onClick={() => onMoveRow(row.id, -1)}><i className="fas fa-chevron-up"></i></button>
                  <button type="button" className="btn btn-outline-secondary btn-sm" title="เลื่อนลง" disabled={index === rows.length - 1} onClick={() => onMoveRow(row.id, 1)}><i className="fas fa-chevron-down"></i></button>
                  <button type="button" className="btn btn-outline-secondary btn-sm" title="แทรกก่อนแถวนี้" onClick={() => onAddRow(row.id, true)}><i className="fas fa-chevron-left"></i></button>
                  <button type="button" className="btn btn-outline-secondary btn-sm" title="เพิ่มถัดจากแถวนี้" onClick={() => onAddRow(row.id, false)}><i className="fas fa-plus"></i></button>
                  <button type="button" className="btn btn-outline-danger btn-sm" title="ลบแถวนี้" onClick={() => onRemoveRow(row.id)}><i className="fas fa-trash-alt"></i></button>
                </div>
              </div>
            ))}
          </div>
        )}

        {!isNotYetSendApprove && (
          <div className="row">
            <div className="col-12">
              <div className="mb-2 d-flex align-items-center" style={{ gap: 6 }}>
                <span>สถานะอนุมัติ:</span>
                <i className={Utils.GetDSignApproveStatusIconClass(data?.approveStatus)} title={data?.approveStatus ?? ""}></i>
                <span>{Utils.GetDSignApproveStatusText(data?.approveStatus)}</span>
                {data?.approveRemark && (<span className="text-danger">เหตุผล : {data.approveRemark}</span>)}
              </div>
              {getApprovalFlowSteps().map((step) => (
                <div key={step.seq} className="d-flex align-items-center mb-1" style={{ gap: 8 }}>
                  <i className={Utils.GetDSignActionIconClass(step.action)} title={step.action}></i>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="text-secondary">{step.roledisplayname || step.rolename || "-"}</div>
                    <div className="fz-10">{step.username}</div>
                    {step.remark && (<div className="fz-10 text-danger">เหตุผล : {step.remark}</div>)}
                  </div>
                  {step.actiondate && (<div className="fz-10 text-secondary" style={{ flex: "0 0 auto" }}>{Utils.GetDMYHHMM(step.actiondate)}</div>)}
                  {step.userid === myUserId && (step.action === "Pending" || step.action === "None") && (
                    <>
                      <button className="btn btn-success btn-sm" onClick={onApproveStep}>Approve</button>
                      <button className="btn btn-danger btn-sm" onClick={onRejectStep}>Reject</button>
                    </>
                  )}
                  {step.userid === myUserId && step.action === "Approved" && data?.approveStatus !== "Finished" && (
                    <button className="btn btn-warning btn-sm" onClick={onRecallStep}>Recall</button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12, borderTop: "dashed 1px var(--m2-line-divider-color)", paddingTop: 8 }}>
          <div style={{ display: "flex", gap: 8 }}>
            {data?.approveStatus === "Open" && (
              <button className="btn btn-outline-danger" onClick={onCancelApproval}>Cancel Request</button>
            )}
            {data?.approveStatus === "Open" && (
              <button className="btn btn-outline-primary" onClick={() => { if (!isBlockedByApproveOutside()) setShowAddApprover(true); }}>เพิ่มผู้อนุมัติ</button>
            )}
            {data?.approveStatus === "Open" && (isAdmin || getApprovalFlowSteps().some(s => s.userid === myUserId)) && (
              <button className="btn btn-outline-danger" onClick={() => { if (!isBlockedByApproveOutside()) setShowRemoveApprover(true); }}>ลบผู้อนุมัติ</button>
            )}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn btn-warning" onClick={() => onClose(null)}>ปิด</button>
            {isNotYetSendApprove && (<button className="btn btn-primary" onClick={onOk}>Send Approval</button>)}
          </div>
        </div>
      </div>
      {showStatusMapping && (
        <StatusMappingPopup
          mapping={statusMapping}
          readOnly={!canEditStatusMapping}
          onClose={(result) => {
            setShowStatusMapping(false);
            if (result) onStatusMappingOk(result);
          }}
        />
      )}
      {showAddApprover && (
        <AddApproverPopup
          taskId={taskId}
          steps={getApprovalFlowSteps()}
          onClose={(added) => {
            setShowAddApprover(false);
            if (added) loadData();
          }}
        />
      )}
      {showRemoveApprover && (
        <RemoveApproverPopup
          taskId={taskId}
          steps={getApprovalFlowSteps()}
          onClose={(removed) => {
            setShowRemoveApprover(false);
            if (removed) loadData();
          }}
        />
      )}
    </div>,
    document.body
  );
}

function StatusMappingPopup({ mapping, readOnly, onClose }: { mapping: StatusMapping | null, readOnly: boolean, onClose: (result: StatusMapping | null) => void }) {
  const [local, setLocal] = useState<StatusMapping>(mapping ?? DEFAULT_STATUS_MAPPING);

  useBackButtonClose(true, () => onClose(null));

  const onChangeRow = (key: ApproveStatusKey, value: string) => {
    setLocal(prev => ({ ...prev, [key]: value === "" ? null : Utils.GetTaskStatusFromVal(Number(value)) }));
  }

  return ReactDOM.createPortal(
    <div style={backdropStyle}>
      <div style={modalStyle}>
        <h3 style={{ marginTop: 0, borderBottom: "dashed 1px var(--bs-secondary)" }}>
          ผูกสถานะ (Status Mapping)
          <div className="fz-10 text-secondary">
            {readOnly
              ? "การอนุมัตินี้จบแล้ว ดูได้อย่างเดียว แก้ไขไม่ได้"
              : "กำหนดว่าเมื่อสถานะการอนุมัติเปลี่ยน จะให้สถานะงานเปลี่ยนตามเป็นอะไร"}
          </div>
        </h3>

        <div className="table-responsive">
          <table className="table table-sm align-middle mb-0">
            <thead>
              <tr>
                <th style={{ width: "45%" }}>สถานะการอนุมัติ (Approve Status)</th>
                <th style={{ width: 40 }}></th>
                <th>สถานะงาน (Task Status)</th>
              </tr>
            </thead>
            <tbody>
              {APPROVE_STATUSES.map(key => (
                <tr key={key}>
                  <td>
                    <div className="d-flex align-items-center" style={{ gap: 6 }}>
                      <i className={Utils.GetDSignApproveStatusIconClass(key)}></i>
                      <span>{APPROVE_STATUS_LABEL[key]}</span>
                      <span className="fz-10 text-secondary">{Utils.GetDSignApproveStatusText(key)}</span>
                    </div>
                  </td>
                  <td className="text-center text-secondary"><i className="fas fa-arrow-right"></i></td>
                  <td>
                    <select className="form-select" disabled={readOnly} value={local[key] ?? ""} onChange={e => onChangeRow(key, e.target.value)}>
                      <option value="">None (ไม่เปลี่ยนสถานะงาน)</option>
                      {Utils.GetTaskStatusForShow().map(ts => (
                        <option key={ts} value={ts}>{`${Utils.GetTaskStatusIcon(ts)} ${Utils.GetTaskStatusText(ts)}`}</option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginTop: 12, borderTop: "dashed 1px var(--m2-line-divider-color)", paddingTop: 8 }}>
          {readOnly
            ? (<span></span>)
            : (<button className="btn btn-outline-secondary" onClick={() => setLocal(DEFAULT_STATUS_MAPPING)}>ค่าเริ่มต้น</button>)}
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn btn-warning" onClick={() => onClose(null)}>ปิด</button>
            {!readOnly && (<button className="btn btn-primary" onClick={() => onClose(local)}>ตกลง</button>)}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function AddApproverPopup({ taskId, steps, onClose }: { taskId: string, steps: ApprovalFlowStep[], onClose: (added: boolean) => void }) {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [userId, setUserId] = useState<number | null>(null);
  const [roleName, setRoleName] = useState<string>(CAPTION_DEFAULT_NEW_ROW);
  // Both start blank so the user has to pick where the new approver goes
  const [referenceUserId, setReferenceUserId] = useState<number | null>(null);
  const [placement, setPlacement] = useState<"Before" | "After" | "">("");

  const stepOptions = steps.map(s => ({ value: s.userid, step: s }));
  const selectedStepOption = stepOptions.find(o => o.value === referenceUserId) ?? null;

  useBackButtonClose(true, () => onClose(false));

  const onUserChanged = (newUserId: number | null) => {
    setUserId(newUserId);
    if (!newUserId) return;
    new APIClient().fnPostForm(url.WP_TK_GET_USER_POSITION, { userId: newUserId })
      .then(rs => setRoleName(rs?.positionName?.trim() || CAPTION_DEFAULT_NEW_ROW))
      .catch(e => Utils.HandleException(e, true));
  }

  const onSubmit = async () => {
    if (!userId) {
      Utils.SweetAlert("กรุณาเลือกผู้อนุมัติที่ต้องการเพิ่ม");
      return;
    }
    if (!placement) {
      Utils.SweetAlert("กรุณาเลือกตำแหน่งที่จะเพิ่ม (ก่อนหน้า / ต่อจาก)");
      return;
    }
    if (!referenceUserId) {
      Utils.SweetAlert("กรุณาเลือกผู้อนุมัติอ้างอิง");
      return;
    }
    if (!roleName.trim()) {
      Utils.SweetAlert("กรุณาระบุชื่อตำแหน่งผู้อนุมัติ");
      return;
    }

    try {
      setIsLoading(true);
      await new APIClient().fnPostForm(url.WP_TK_ADD_APPROVER_STEP, { taskId, userId, roleName: roleName.trim(), referenceUserId, placement });
      setIsLoading(false);
      onClose(true);
    } catch (e) {
      setIsLoading(false);
      Utils.HandleException(e, true);
    }
  }

  return ReactDOM.createPortal(
    <div style={backdropStyle}>
      {isLoading && (<Loader />)}
      <div style={modalStyle}>
        <h3 style={{ marginTop: 0, borderBottom: "dashed 1px var(--bs-secondary)" }}>เพิ่มผู้อนุมัติเข้าร่วมขั้นตอน</h3>

        <div className="mb-2">
          <div className="fz-10 text-secondary">ผู้อนุมัติที่ต้องการเพิ่ม</div>
          <SelectDropdownEmp isDisabled={false} selectedUserId={userId} onUserChanged={onUserChanged} />
        </div>

        <div className="mb-2">
          <div className="fz-10 text-secondary">ชื่อตำแหน่ง</div>
          <input type="text" className="form-control" value={roleName} onChange={e => setRoleName(e.target.value)} />
        </div>

        <div className="mb-2">
          <div className="fz-10 text-secondary">ตำแหน่งที่จะเพิ่ม</div>
          <div className="d-flex align-items-center" style={{ gap: 8 }}>
            <select className="form-select" style={{ flex: "0 0 auto", width: "auto" }} value={placement} onChange={e => setPlacement(e.target.value as "Before" | "After" | "")}>
              <option value="">-- เลือก --</option>
              <option value="Before">ก่อนหน้า</option>
              <option value="After">ต่อจาก</option>
            </select>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Select
                options={stepOptions}
                value={selectedStepOption}
                onChange={(o: any) => setReferenceUserId(o?.value ?? null)}
                getOptionValue={(o: any) => String(o.value)}
                getOptionLabel={(o: any) => o.step.username}
                formatOptionLabel={(o: any) => (
                  <div className="d-flex align-items-center" style={{ gap: 6, padding: "4px 8px" }}>
                    <i className={Utils.GetDSignActionIconClass(o.step.action)} title={o.step.action}></i>
                    <div style={{ minWidth: 0 }}>
                      <div>{o.step.username}</div>
                      <div className="fz-10 text-secondary">
                        {o.step.roledisplayname || o.step.rolename || "-"} · {Utils.GetDSignActionText(o.step.action)}
                      </div>
                    </div>
                  </div>
                )}
                styles={Utils.GetThemeForReactSelect()}
                placeholder="-- เลือกผู้อนุมัติ --"
                isClearable={true}
                isSearchable={false}
                menuPlacement="auto"
                menuPosition="fixed"
                menuPortalTarget={document.body}
              />
            </div>
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12, borderTop: "dashed 1px var(--m2-line-divider-color)", paddingTop: 8 }}>
          <button className="btn btn-warning" onClick={() => onClose(false)}>ปิด</button>
          <button className="btn btn-primary" onClick={onSubmit}>เพิ่ม</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function RemoveApproverPopup({ taskId, steps, onClose }: { taskId: string, steps: ApprovalFlowStep[], onClose: (removed: boolean) => void }) {
  const removableSteps = steps.filter(s => s.rolename !== CAPTION_SUBMITTER);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [userId, setUserId] = useState<number | null>(removableSteps[0]?.userid ?? null);
  const [reason, setReason] = useState<string>("");

  useBackButtonClose(true, () => onClose(false));

  const selectedStep = removableSteps.find(s => s.userid === userId) ?? null;

  const onSubmit = async () => {
    if (!userId) {
      Utils.SweetAlert("กรุณาเลือกผู้อนุมัติที่ต้องการลบ");
      return;
    }
    if (!reason.trim()) {
      Utils.SweetAlert("กรุณาระบุเหตุผลที่ลบผู้อนุมัติ");
      return;
    }

    const alreadyActed = selectedStep && selectedStep.action !== "None" && selectedStep.action !== "Pending";
    if (alreadyActed) {
      const ok = window.confirm(`${selectedStep!.username} ได้ดำเนินการอนุมัติไปแล้ว (${Utils.GetDSignActionText(selectedStep!.action)}) ต้องการลบออกจากขั้นตอนอนุมัติหรือไม่?`);
      if (!ok) return;
    }

    try {
      setIsLoading(true);
      await new APIClient().fnPostForm(url.WP_TK_REMOVE_APPROVER_STEP, { taskId, userId, reason: reason.trim() });
      setIsLoading(false);
      onClose(true);
    } catch (e) {
      setIsLoading(false);
      Utils.HandleException(e, true);
    }
  }

  return ReactDOM.createPortal(
    <div style={backdropStyle}>
      {isLoading && (<Loader />)}
      <div style={modalStyle}>
        <h3 style={{ marginTop: 0, borderBottom: "dashed 1px var(--bs-secondary)" }}>ลบผู้อนุมัติออกจากขั้นตอน</h3>

        <div className="mb-2">
          <div className="fz-10 text-secondary">ผู้อนุมัติที่ต้องการลบ</div>
          <select className="form-select" value={userId ?? ""} onChange={e => setUserId(e.target.value ? Number(e.target.value) : null)}>
            {removableSteps.map(s => (<option key={s.seq} value={s.userid}>{s.username} ({s.roledisplayname || s.rolename}){s.action !== "None" && s.action !== "Pending" ? ` - ${Utils.GetDSignActionText(s.action)}` : ""}</option>))}
          </select>
        </div>

        <div className="mb-2">
          <div className="fz-10 text-secondary">เหตุผลที่ลบ</div>
          <textarea className="form-control" rows={2} value={reason} onChange={e => setReason(e.target.value)} />
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12, borderTop: "dashed 1px var(--m2-line-divider-color)", paddingTop: 8 }}>
          <button className="btn btn-warning" onClick={() => onClose(false)}>ปิด</button>
          <button className="btn btn-danger" onClick={onSubmit}>ลบ</button>
        </div>
      </div>
    </div>,
    document.body
  );
}
